import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";

export interface FleetIconStates {
  engine_on: string | null;
  engine_off: string | null;
  offline: string | null;
}

export interface FleetIcon {
  id: string;
  name: string;
  states?: Partial<FleetIconStates> | null;
  car?: string | null;
  /**
   * Alto entre ancho del vehículo dentro del sprite, medido por
   * `scripts/punteros-unidades.cjs` al subirlo. El mapa lo usa para dibujar la
   * cápsula del estado pegada a la silueta.
   */
  aspect?: number | null;
  /**
   * Largo real del modelo en metros, también medido al subir. Con él el mapa
   * dibuja el puntero a la escala del terreno: un tractocamión se ve tan largo
   * como es, no igual que un auto por llenar el mismo lienzo.
   */
  largoM?: number | null;
}

interface FleetIconListBody {
  data?: FleetIcon[];
}

export const fleetIconImage = (icon: FleetIcon): string | null =>
  icon.car || icon.states?.engine_off || icon.states?.engine_on || icon.states?.offline || null;

/**
 * Estado de la unidad -> arte del catálogo. El vocabulario del mapa
 * (driving/ralenti/stopped/sleep/offline) es más fino que el del catálogo
 * (engine_on/engine_off/offline), así que varios estados comparten sprite.
 */
const MARKER_STATE_BY_STATUS: Record<string, keyof FleetIconStates> = {
  driving: "engine_on",
  ralenti: "engine_on",
  idle: "engine_on",
  stopped: "engine_off",
  sleep: "engine_off",
  offline: "offline",
};

/**
 * Sprite que va en el puntero del mapa para un estado dado.
 *
 * A diferencia de `fleetIconImage`, NO cae a `icon.car`: esa es la foto de
 * catálogo en tres cuartos y en el mapa saldría de cabeza al rotar con el
 * rumbo. Si el icono no tiene arte de puntero devuelve `null` y el mapa pinta
 * el glifo clásico.
 */
export const fleetIconMarker = (
  icon: FleetIcon | undefined | null,
  status: string | null | undefined,
): string | null => {
  if (!icon?.states) return null;
  const preferido = MARKER_STATE_BY_STATUS[(status ?? "").toString()];
  const { engine_on, engine_off, offline } = icon.states;
  return (
    (preferido ? icon.states[preferido] : null) || engine_on || engine_off || offline || null
  );
};

/**
 * Catálogo indexado por id. El mapa lo arma una sola vez y resuelve el sprite
 * de cada unidad con `fleetIconMarker(index.get(icon_id), status)`, en vez de
 * suscribir un hook por marcador (son cientos).
 */
export const fleetIconIndex = (icons: readonly FleetIcon[]): Map<string, FleetIcon> =>
  new Map(icons.map((icon) => [icon.id, icon]));

export function useIconsList({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    queryKey: qk.icons.list(),
    queryFn: ({ signal }) =>
      getJson<FleetIconListBody>("/iconsList", { page: 1, limit: 100 }, { signal }).then(
        (body) => body.data ?? [],
      ),
    staleTime: 10 * 60 * 1000,
  });
}
